import { PaymentScheduleType } from '../../state/configuration'
import { PolicyVersion } from './policy'
import { RiskProfileStatus } from './riskProfile'

export enum PolicyChangeType {
  ASSET_DISCLOSURE_CHANGE = 'ASSET_DISCLOSURE_CHANGE',
  PROFILE_DISCLOSURE_CHANGE = 'PROFILE_DISCLOSURE_CHANGE',
  POLICYHOLDER_DISCLOSURE_CHANGE = 'POLICYHOLDER_DISCLOSURE_CHANGE',
  ADD_PROFILE = 'ADD_PROFILE',
  ADDITIONAL_PROFILE_CREATE_CHANGE = 'ADDITIONAL_PROFILE_CREATE_CHANGE',
  REMOVE_PROFILE = 'REMOVE_PROFILE',
  ADD_COVER = 'ADD_COVER',
  REMOVE_COVER = 'REMOVE_COVER',
  SELECT_VOLUNTARY_EXCESS = 'SELECT_VOLUNTARY_EXCESS',
  REQUEST_COVER_OPTION = 'REQUEST_COVER_OPTION',
  SELECT_PAYMENT_SCHEDULE = 'SELECT_PAYMENT_SCHEDULE',
  SELECT_COVER_LEVEL = 'SELECT_COVER_LEVEL',
}

export interface JsonUpdate {
  path: string
  value?: unknown
  attribute?: boolean
}

export interface PolicyChange {
  changeType: PolicyChangeType
}

export interface AddProfileChange extends PolicyChange {
  changeType: PolicyChangeType.ADD_PROFILE
  profileCode: string
}

export interface AdditionalProfileCreateChange extends PolicyChange {
  changeType: PolicyChangeType.ADDITIONAL_PROFILE_CREATE_CHANGE
  profileCode: string
}

export interface RemoveProfileChange extends PolicyChange {
  changeType: PolicyChangeType.REMOVE_PROFILE
  profileCode: string
}

export interface SelectVoluntaryExcessChange extends PolicyChange {
  changeType: PolicyChangeType.SELECT_VOLUNTARY_EXCESS
  excess: number
  excessCode: string
}

export interface PolicyholderDisclosureChange extends PolicyChange {
  changeType: PolicyChangeType.POLICYHOLDER_DISCLOSURE_CHANGE
  update: JsonUpdate
}

export interface ProfileDisclosureChange extends PolicyChange {
  changeType: PolicyChangeType.PROFILE_DISCLOSURE_CHANGE
  profileCode: string
  update: JsonUpdate
}

export interface AssetDisclosureChange extends PolicyChange {
  changeType: PolicyChangeType.ASSET_DISCLOSURE_CHANGE
  update: JsonUpdate
}

export interface AddCoverDisclosureChange extends PolicyChange {
  changeType: PolicyChangeType.ADD_COVER
  coverOption: string
}

export interface RemoveCoverDisclosureChange extends PolicyChange {
  changeType: PolicyChangeType.REMOVE_COVER
  coverOption: string
}

export interface RequestCoverOptionChange extends PolicyChange {
  changeType: PolicyChangeType.REQUEST_COVER_OPTION
  coverOption: string
  selected?: boolean
}

export interface PaymentScheduleTypeChange extends PolicyChange {
  changeType: PolicyChangeType.SELECT_PAYMENT_SCHEDULE
  paymentScheduleType: PaymentScheduleType
}

export interface SelectCoverLevelChange extends PolicyChange {
  changeType: PolicyChangeType.SELECT_COVER_LEVEL
  coverLevel: string
}

export interface ProposalAdjustmentRequest {
  applyRating?: boolean
  coverStart?: string | null
  changes: Array<
    | AdditionalProfileCreateChange
    | AddProfileChange
    | AssetDisclosureChange
    | ProfileDisclosureChange
    | PolicyholderDisclosureChange
    | RemoveProfileChange
    | AddCoverDisclosureChange
    | RemoveCoverDisclosureChange
    | SelectVoluntaryExcessChange
    | RequestCoverOptionChange
    | PaymentScheduleTypeChange
    | SelectCoverLevelChange
  >
}

export type PolicyAdjustmentChange =
  | AddProfileChange
  | ProfileDisclosureChange
  | AdditionalProfileCreateChange
  | AssetDisclosureChange
  | PolicyholderDisclosureChange
  | RemoveProfileChange
  | AddCoverDisclosureChange
  | RemoveCoverDisclosureChange
  | SelectVoluntaryExcessChange
  | RequestCoverOptionChange

export interface PolicyAdjustmentRequest {
  policyId: string
  effectiveAt?: string | null
  changes: Array<PolicyAdjustmentChange>
  reason?: string
  adjustmentType: 'SDA' | 'MTA' | 'CAN'
}

export interface PolicyAdjustmentResponse {
  adjustmentId: string
  policyId: string
  confirmed: boolean
  verificationClear: boolean
  updatedPolicyVersions: PolicyVersion[]
  status: RiskProfileStatus

  prorataPremium: number
  availableBalance: number
  amountDue: number

  changes: PolicyAdjustmentRequest['changes']
}
